'use client';

// npm library imports
import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';

// style imports
import styles from './threeD.module.scss';
import emblaStyles from './embla.module.scss';

// local component imports
import Icon from '@components/Icons';
import SingleImageCarousel from '@components/Carousels/_SingleImageCarousel';

// type imports
import type { CarouselImage } from '@lib/types';

/**
 * Slide component for the 3D carousel.
 *
 * @remarks
 * Memoized component to prevent unnecessary re-renders.
 */
const _Slide = React.memo(function _Slide({
    image,
    slideClassName,
}: {
    image: CarouselImage;
    slideClassName: string;
}) {
    return (
        <div className={`${styles.ThreeD__slide} ${slideClassName}`}>
            <SingleImageCarousel image={image} className={styles.ThreeDimg} />
        </div>
    );
});

/**
 * 3D Carousel component that displays 3 images in a 3D carousel.
 *
 * @remarks
 * This component can have more than 3 images, but any extra will not be seen on screen uintil they rotate into view.
 * Component contains logic for mobile touch swiping, and pausing autoplay on iteraction for better UX.
 *
 * @param props - The component props object
 * @param props.images - The array of CarouselImages to display in the carousel
 * @param props.overlap Optional - Used to position the carousel overlapping other sections
 * @param props.interval Optional - The interval in milliseconds between each slide
 * @returns 3D carousel component
 *
 * @example
 * ```tsx
 * <CarouselThreeD
 *  images={images}
 *  overlap="top"
 * />
 * ```
 */
const CarouselThreeD = ({
    images,
    overlap = 'none',
    interval = 10000,
}: {
    images: CarouselImage[];
    overlap?: 'top' | 'bottom' | 'none';
    interval?: number;
}) => {
    // State to keep track of the current slide index
    const [currentIndex, setCurrentIndex] = useState(0);

    // Refs to keep track of swipping values
    // Refs were used to persist values between render cycles without causing re-renders.
    const startX = useRef<number | null>(null);
    const autoPlayTimerRef = useRef<number | null>(null);
    const hasSwipedRef = useRef(false);

    // Function to move the carousel left or right
    const moveCarousel = useCallback(
        (direction: 'left' | 'right') => {
            setCurrentIndex((prevIndex) => {
                if (direction === 'left') {
                    return (prevIndex + 1) % images.length;
                } else {
                    return (prevIndex - 1 + images.length) % images.length;
                }
            });
        },
        [images.length],
    );

    // Function to get the class name for the slide based on the current index
    const getSlideClassName = useCallback(
        (index: number): string => {
            const offset =
                (index - currentIndex + images.length) % images.length;
            switch (offset) {
                case 0:
                    return styles.centerSlide;
                case 1:
                    return styles.rightSlide;
                case images.length - 1:
                    return styles.leftSlide;
                default:
                    return styles.hiddenSlide;
            }
        },
        [currentIndex, images.length],
    );

    // Memoized slides array
    const slides: React.JSX.Element[] = useMemo(
        () =>
            images.map((image, index) => (
                <_Slide
                    key={index}
                    image={image}
                    slideClassName={getSlideClassName(index)}
                />
            )),
        [images, getSlideClassName],
    );

    /**
     * AutoPlay Functions
     */

    // Function to start auto play
    const startAutoPlay = useCallback(() => {
        // If autoPlayTimerRef is null, start auto play
        if (autoPlayTimerRef.current === null) {
            autoPlayTimerRef.current = window.setInterval(
                // Move carousel left every interval
                () => moveCarousel('left'),
                interval,
            );
        }
    }, [moveCarousel, interval]);

    // Function to stop auto play
    const stopAutoPlay = useCallback(() => {
        // If autoPlayTimerRef is not null, clear interval, and set to null
        if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
            autoPlayTimerRef.current = null;
        }
    }, []);

    // Effect to start and stop auto play on mount and unmount
    useEffect(() => {
        startAutoPlay();
        return stopAutoPlay;
    }, [startAutoPlay, stopAutoPlay]);

    /**
     * Swipe Interaction Functions
     */

    // Interaction Start event handler to track potential swipe
    const handleInteractionStart = useCallback(
        (clientX: number) => {
            //Set touch start and current position
            startX.current = clientX;
            hasSwipedRef.current = false;
            stopAutoPlay(); // stop auto play on interaction start
        },
        [stopAutoPlay],
    );

    // Check if there is enough swipe distance to be considered a swipe
    const handleInteractionMove = useCallback(
        (clientX: number) => {
            // If startX is not null and hasSwiped is false, calculate swipe distance
            if (startX.current !== null && !hasSwipedRef.current) {
                const diff = startX.current - clientX; // Calculate swipe distance
                const swipeThreshold = 50; // threshold to consider a swipe

                // If swipe distance is greater than swipeThreshold, swipe in correct direction
                if (Math.abs(diff) > swipeThreshold) {
                    if (diff > 0) {
                        moveCarousel('left');
                    } else {
                        moveCarousel('right');
                    }
                    hasSwipedRef.current = true; // reset hasSwiped
                }
            }
        },
        [moveCarousel],
    );

    // End interaction event handler to clean up swipe values
    const handleInteractionEnd = useCallback(() => {
        startX.current = null;
        hasSwipedRef.current = false;
        startAutoPlay();
    }, [startAutoPlay]);

    /**
     * Carousel Event Handlers
     * These functions handle the touch and mouse events for the carousel.
     */

    // Touch start
    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            handleInteractionStart(e.touches[0].clientX);
        },
        [handleInteractionStart],
    );

    // Touch move
    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            handleInteractionMove(e.touches[0].clientX);
        },
        [handleInteractionMove],
    );

    // Mouse down
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            handleInteractionStart(e.clientX);
        },
        [handleInteractionStart],
    );

    // Mouse move
    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (startX.current !== null) {
                // Only handle move if the mouse is down
                handleInteractionMove(e.clientX);
            }
        },
        [handleInteractionMove],
    );

    return (
        <div
            /* 3D Carousel container */
            className={`${styles.ThreeD} ${emblaStyles.carouselThreeD} ${emblaStyles[overlap]}`}
            style={{ maxHeight: `${images[0].height}px` }}
            /* Touch event handlers */
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleInteractionEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleInteractionEnd}
            onMouseLeave={handleInteractionEnd}
        >
            {/* Render Carousel slides array */}
            {slides}
             {/* Navigation Arrows */}
             {/*
            <button
                className={`${emblaStyles.embla__button} ${emblaStyles.embla__button_prev}`}
                onClick={() => moveCarousel('right')}
                aria-label="Previous slide"
            >
                <Icon
                    iconClass="nav-arrow-left"
                    externalStlyes={emblaStyles.embla__button__svg}
                />
            </button>
            */}
            {/* 
            <button
                className={`${emblaStyles.embla__button} ${emblaStyles.embla__button_next}`}
                onClick={() => moveCarousel('left')}
                aria-label="Next slide"
            >
                <Icon
                    iconClass="nav-arrow-right"
                    externalStlyes={emblaStyles.embla__button__svg}
                />
            </button>
            */}
        </div>
    );
};

export default CarouselThreeD;
